import React, { useState, useEffect } from 'react';
import { Brain, GitCompare, FileText, Link, Copy, ExternalLink, Target, Users } from 'lucide-react';
import { useOptimisticNavigation } from '@/hooks/useOptimisticNavigation';
import { useCompanyInfo } from '@/hooks/useCompanyInfo';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { generateRegistrationLink, generateWorkBehaviorLink, generateCognitiveAssessmentLink, copyToClipboard } from '@/utils/registrationLinks';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface QuickActionCardsProps {
  totalCounts?: { behavioral: number; idealProfiles: number };
  pendingCount?: number;
  hasIdealProfile?: boolean;
}

const QuickActionCards: React.FC<QuickActionCardsProps> = ({
  totalCounts = { behavioral: 0, idealProfiles: 0 },
  pendingCount = 0,
  hasIdealProfile = false,
}) => {
  const { navigateWithFeedback } = useOptimisticNavigation();
  const { companyInfo } = useCompanyInfo();
  const { toast } = useToast();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [companySlug, setCompanySlug] = useState<string | null>(null);

  useEffect(() => {
    const fetchSlug = async () => {
      if (!companyInfo?.id) return;
      const { data } = await supabase.from('companies').select('slug').eq('id', companyInfo.id).single();
      setCompanySlug(data?.slug || null);
    };
    fetchSlug();
  }, [companyInfo?.id]);

  const handleCopyLink = async (type: 'registration' | 'workBehavior' | 'cognitive') => {
    if (!companySlug) return;
    const linkMap = {
      registration: generateRegistrationLink(companySlug),
      workBehavior: generateWorkBehaviorLink(companySlug),
      cognitive: generateCognitiveAssessmentLink(companySlug),
    };
    const labelMap = { registration: 'Full Assessment', workBehavior: 'Work Behavior', cognitive: 'Cognitive' };
    const success = await copyToClipboard(linkMap[type]);
    toast({
      title: success ? `${labelMap[type]} link copied!` : 'Copy failed',
      description: success ? 'Paste it into your invite email or ATS.' : 'Please try again.',
      variant: success ? 'default' : 'destructive',
    });
  };

  const handleOpenLink = (type: 'registration' | 'workBehavior' | 'cognitive') => {
    if (!companySlug) return;
    const linkMap = {
      registration: generateRegistrationLink(companySlug),
      workBehavior: generateWorkBehaviorLink(companySlug),
      cognitive: generateCognitiveAssessmentLink(companySlug),
    };
    window.open(linkMap[type], '_blank');
  };

  // Context-aware primary action
  const hasPendingCandidates = pendingCount > 0 && totalCounts.behavioral > 0;
  const needsIdealProfile = !hasIdealProfile && totalCounts.behavioral > 0;

  const primaryAction = hasPendingCandidates
    ? {
        title: `Review Pending (${pendingCount})`,
        description: 'Candidates awaiting review',
        icon: Users,
        onClick: () => navigateWithFeedback('/dashboard?tab=behavioral'),
        gradient: 'from-orange-500 to-rose-500',
      }
    : {
        title: 'New Assessment',
        description: 'Start a new candidate assessment',
        icon: Brain,
        onClick: () => navigateWithFeedback('/assessment'),
        gradient: 'from-primary to-accent',
      };

  const secondaryAction = needsIdealProfile
    ? {
        title: 'Create Ideal Profile',
        description: 'Required for candidate matching',
        icon: Target,
        onClick: () => navigateWithFeedback('/ideal/new'),
        gradient: 'from-purple-500 to-indigo-600',
      }
    : {
        title: 'Compare Profiles',
        description: 'Side-by-side comparison',
        icon: GitCompare,
        onClick: () => navigateWithFeedback('/comparison'),
        gradient: 'from-secondary to-purple-600',
      };

  const actions = [
    primaryAction,
    secondaryAction,
    {
      title: 'View All Results',
      description: 'Browse all assessments',
      icon: FileText,
      onClick: () => navigateWithFeedback('/my-results'),
      gradient: 'from-success to-emerald-500',
    },
    {
      title: 'Invite Candidates',
      description: 'Copy assessment links',
      icon: Link,
      onClick: () => setInviteOpen(true),
      gradient: 'from-orange-500 to-rose-600',
    },
  ];

  return (
    <>
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`group relative p-6 min-h-[110px] rounded-xl border border-transparent bg-gradient-to-br ${action.gradient} hover:shadow-2xl hover:scale-105 transition-all duration-300 text-left overflow-hidden touch-manipulation active:scale-100`}
            >
              <div className="absolute inset-0 bg-background opacity-0 group-hover:opacity-10 transition-opacity" />
              <div className="relative z-10">
                <div className="p-2.5 rounded-lg bg-background/20 backdrop-blur-sm w-fit mb-3">
                  <action.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-sm md:text-base font-bold text-white leading-tight">{action.title}</h3>
                <p className="text-xs text-white/80 mt-1 leading-relaxed">{action.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Invite Candidates Modal */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="h-5 w-5 text-primary" />
              Invite Candidates
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-muted-foreground">
              Share one of these links with your candidates to start their assessment.
            </p>

            {/* Full Assessment */}
            <div className="border rounded-lg p-4 bg-primary/5 border-primary/20">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <p className="font-semibold text-sm">Full Assessment</p>
                  <p className="text-xs text-muted-foreground">Behavioral + Cognitive — recommended</p>
                </div>
                <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full">Recommended</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => handleCopyLink('registration')}>
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Link
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleOpenLink('registration')}>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Work Behavior Only */}
            <div className="border rounded-lg p-4">
              <div>
                <p className="font-semibold text-sm">Work Behavior Only</p>
                <p className="text-xs text-muted-foreground">Behavioral assessment only</p>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => handleCopyLink('workBehavior')}>
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Link
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleOpenLink('workBehavior')}>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Cognitive Only */}
            <div className="border rounded-lg p-4">
              <div>
                <p className="font-semibold text-sm">Cognitive Only</p>
                <p className="text-xs text-muted-foreground">Cognitive assessment only</p>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => handleCopyLink('cognitive')}>
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Link
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleOpenLink('cognitive')}>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickActionCards;
